import type { ResourceReservation } from '../../../__generated__/types';
import type { GetCurrentUserReservationsByDateWorkWeekReservations } from '../graphql/__generated__/useCustomResourceReservations.generated';

export type TemplateCategory = { plural: string; singular: string };

function derivePluralFromName(name: string): string {
  const base = name.replace(/\s*\d+$/i, '').trim();
  if (base.endsWith('y') && !/[aeiou]y$/i.test(base))
    return base.slice(0, -1) + 'ies';
  if (base.endsWith('s')) return base;
  return base + 's';
}

export function groupCustomResources(
  reservations: GetCurrentUserReservationsByDateWorkWeekReservations[],
  templateIdToCategory?: Map<string, TemplateCategory>
): Array<{
  label: string;
  items: GetCurrentUserReservationsByDateWorkWeekReservations[];
}> {
  const map = new Map<
    string,
    GetCurrentUserReservationsByDateWorkWeekReservations[]
  >();

  for (const res of reservations) {
    const tpl = res.resource.originatingCustomResourceTemplateId
      ? templateIdToCategory?.get(
          res.resource.originatingCustomResourceTemplateId
        )
      : undefined;
    const groupName = res.resource.resourceGroupName?.trim();
    const label =
      tpl?.plural || groupName || derivePluralFromName(res.resource.name);

    const arr = map.get(label) ?? [];
    arr.push(res);
    map.set(label, arr);
  }

  // Sort groups: system defaults first (Lockers, Parking spots), then A-Z
  const entries = Array.from(map.entries()).map(([label, items]) => ({
    label,
    items,
  }));
  entries.sort((a, b) => a.label.localeCompare(b.label));

  const take = (pred: (l: string) => boolean) => {
    const idx = entries.findIndex((e) => pred(e.label.toLowerCase()));
    return idx >= 0 ? entries.splice(idx, 1)[0] : undefined;
  };

  const ordered: Array<{
    label: string;
    items: GetCurrentUserReservationsByDateWorkWeekReservations[];
  }> = [];
  const lockers = take((l) => l === 'lockers');
  const parking = take((l) => l === 'parking spots' || l === 'parking');
  if (lockers) ordered.push(lockers);
  if (parking) ordered.push(parking);
  ordered.push(...entries);

  // Sort items within each group by start time
  ordered.forEach((g) =>
    g.items.sort((a, b) => a.startTime.localeCompare(b.startTime))
  );

  return ordered;
}
